/**
 * ChartWidget — rekordy (lub kwota) w czasie (recharts). Encja per-firma + okres (przełącznik
 * Tydzień | Miesiąc | Rok w nagłówku kafelka) + wygląd (obszarowy / słupkowy / liniowy).
 *
 * Wzór Figmy „08.6": nad wykresem duża wartość łączna + chip trendu względem poprzedniego okresu
 * i legenda; opcjonalna przerywana linia „poprzedni okres" (porównanie rok do roku).
 *
 * Jedna firma → jedna seria. Tryb „Wszystkie firmy" → osobna seria na każdą firmę
 * (słupki/obszar skumulowane, linie osobno) z legendą. Backend zwraca tylko niepuste
 * kubełki; tutaj dopełniamy luki zerami, żeby oś czasu była ciągła (bez tego dwa
 * odległe miesiące wyglądają na sąsiednie). Kolory: tokeny `chart/1–6` z Figmy
 * (jasny/ciemny wariant przez `useChartTheme`).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useActiveCompanyScope } from '@/lib/use-active-company';
import { type ChartTheme, useChartTheme } from '@/lib/use-chart-theme';

import { type ChartCompanySeries, classifyDataError, fetchChart } from '../dashboard-api';
import {
  AMOUNT_ENTITIES,
  type ChartBucket,
  type ChartVariant,
  type WidgetRenderProps,
} from '../types';
import { DeltaChip, deltaPercent, formatMoney } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

// Kubełki liczy Postgres (`date_trunc` w UTC) — czytamy je w UTC, żeby etykieta nie
// przeskakiwała o dzień/miesiąc w zależności od strefy przeglądarki.
function formatBucket(date: Date, bucket: ChartBucket): string {
  if (bucket === 'month') {
    return date.toLocaleDateString('pl-PL', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

function nextBucket(date: Date, bucket: ChartBucket): Date {
  const next = new Date(date);
  if (bucket === 'month') next.setUTCMonth(next.getUTCMonth() + 1);
  else next.setUTCDate(next.getUTCDate() + (bucket === 'week' ? 7 : 1));
  return next;
}

/** Wiersz wykresu: etykieta osi + wartości po kluczu serii (np. `count` albo id firmy). */
export type ChartRow = { label: string } & Record<string, number | string>;

/**
 * Ciągła oś od najwcześniejszego kubełka do bieżącego okresu; brakujące kubełki = 0.
 * `seriesByKey` = klucz serii → punkty (jedna seria `count` albo po jednej na firmę).
 */
export function fillGaps(
  seriesByKey: Record<string, { bucket: string; count: number }[]>,
  bucket: ChartBucket,
  now: Date = new Date(),
): ChartRow[] {
  const keys = Object.keys(seriesByKey);
  const values = new Map<string, Map<number, number>>();
  const allTimes = new Set<number>();
  for (const key of keys) {
    const m = new Map<number, number>();
    for (const p of seriesByKey[key]!) {
      const t = new Date(p.bucket).getTime();
      if (Number.isNaN(t)) continue;
      m.set(t, p.count);
      allTimes.add(t);
    }
    values.set(key, m);
  }
  if (allTimes.size === 0) return [];

  const sorted = [...allTimes].sort((a, b) => a - b);
  const last = Math.max(sorted[sorted.length - 1]!, now.getTime());
  const out: ChartRow[] = [];
  let cursor = new Date(sorted[0]!);
  // Limit bezpieczeństwa: dzienny wykres z długiego okresu nie może zalać DOM-u.
  for (let i = 0; i < 400 && cursor.getTime() <= last; i++) {
    const row: ChartRow = { label: formatBucket(cursor, bucket) };
    for (const key of keys) row[key] = values.get(key)!.get(cursor.getTime()) ?? 0;
    out.push(row);
    cursor = nextBucket(cursor, bucket);
  }
  return out;
}

/** Zgodność wstecz: jedna seria łączna jako `{ label, count }`. */
export function fillBucketGaps(
  series: { bucket: string; count: number }[],
  bucket: ChartBucket,
  now: Date = new Date(),
): { label: string; count: number }[] {
  return fillGaps({ count: series }, bucket, now) as { label: string; count: number }[];
}

/** Grupowanie dobrane do okresu: rok i całość → miesiące, krótsze okresy → dni. */
export function defaultBucket(period: string): ChartBucket {
  return period === 'year' || period === 'all' ? 'month' : 'day';
}

interface ChartSeriesDef {
  key: string;
  name: string;
  color: string;
  /** Seria porównawcza (poprzedni okres) — rysowana przerywaną linią. */
  dashed?: boolean;
}

const AXIS_TICK = { fontSize: 11, fill: 'rgb(var(--text-tertiary))' };
const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid rgb(var(--border-secondary))',
  background: 'rgb(var(--bg-elevated))',
  color: 'rgb(var(--text-primary))',
};
const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };

const COMPACT = new Intl.NumberFormat('pl-PL', { notation: 'compact', maximumFractionDigits: 1 });

function renderChart(
  variant: ChartVariant,
  data: ChartRow[],
  series: ChartSeriesDef[],
  theme: ChartTheme,
  opts: { stacked: boolean; amount: boolean; showLegend: boolean },
) {
  const fmt = (v: unknown) =>
    opts.amount ? formatMoney(Number(v)) : Number(v).toLocaleString('pl-PL');
  return (
    <ComposedChart data={data} margin={CHART_MARGIN}>
      <defs>
        {series.map((s) => (
          <linearGradient key={s.key} id={`dash-chart-fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={s.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} vertical={false} />
      <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
      <YAxis
        allowDecimals={false}
        tick={AXIS_TICK}
        tickLine={false}
        axisLine={false}
        width={opts.amount ? 48 : 32}
        tickFormatter={(v: number) => (opts.amount ? COMPACT.format(v) : String(v))}
      />
      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmt(v)} />
      {opts.showLegend && <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />}
      {series.map((s, i) => {
        if (variant === 'bar') {
          return (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              fill={s.color}
              fillOpacity={s.dashed ? 0.45 : 1}
              stackId={opts.stacked ? 'companies' : undefined}
              radius={opts.stacked ? (i === series.length - 1 ? [4, 4, 0, 0] : 0) : [4, 4, 0, 0]}
              maxBarSize={32}
            />
          );
        }
        if (variant === 'area' && !s.dashed) {
          return (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={2}
              stackId={opts.stacked ? 'companies' : undefined}
              fill={`url(#dash-chart-fill-${s.key})`}
              dot={false}
              activeDot={{ r: 4 }}
            />
          );
        }
        return (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={s.dashed ? 1.5 : 2}
            strokeDasharray={s.dashed ? '4 3' : undefined}
            dot={s.dashed ? false : { r: 3, fill: s.color }}
            activeDot={{ r: 5 }}
          />
        );
      })}
    </ComposedChart>
  );
}

export function ChartWidget({ widget, period = 'year' }: WidgetRenderProps) {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const { isAll, mode, companyId } = useActiveCompanyScope();

  const cfg = widget.type === 'chart' ? widget.config : null;
  const entity = cfg?.entity ?? null;
  const bucket: ChartBucket = cfg?.bucket ?? defaultBucket(period);
  const variant: ChartVariant = cfg?.variant ?? 'area';
  // Kwota brutto tylko dla faktur; dla pozostałych encji zawsze liczba rekordów.
  const amount = cfg?.measure === 'amount' && entity !== null && AMOUNT_ENTITIES.includes(entity);
  const compare = cfg?.compare === true && !isAll;
  // „Wszystkie firmy" → serie per firma (scope=all); bez żadnej firmy nie ma czego rysować —
  // krótko spinamy zamiast wysyłać zapytanie skazane na 400 MISSING_HEADER.
  const blocked = entity !== null && mode === 'none';

  // Scope firmy w kluczu — defense-in-depth: dziś przełącznik firmy odświeża
  // kafelki blankietowym invalidateQueries w CompanySwitcher, ale bez firmy w
  // queryKey przetrwały cache dałby dane poprzedniej firmy, gdyby to
  // invalidateQueries kiedyś zniknęło albo się prześcignęło z fetchem.
  const query = useQuery({
    queryKey: [
      'tenant',
      'dashboard',
      'chart',
      entity,
      period,
      bucket,
      amount ? 'amount' : 'count',
      compare,
      companyId ?? 'all',
    ],
    queryFn: () =>
      fetchChart({
        entity: entity!,
        period,
        bucket,
        scope: isAll ? 'all' : 'company',
        measure: amount ? 'amount' : 'count',
        compare,
      }),
    enabled: entity !== null && !blocked,
    staleTime: 60_000,
    retry: false,
  });

  const entityName = entity ? t(`tenant.dashboard.metrics.${entity}`) : '';
  const byCompany: ChartCompanySeries[] | undefined = isAll ? query.data?.byCompany : undefined;
  const hasPrevious = compare && (query.data?.previousSeries?.length ?? 0) > 0;
  const currentLabel = period === 'year' ? String(new Date().getFullYear()) : entityName;
  const previousLabel =
    period === 'year'
      ? String(new Date().getFullYear() - 1)
      : t('tenant.dashboard.widgets.chart.legendPrevious');

  const { data, series } = useMemo(() => {
    if (byCompany) {
      const defs: ChartSeriesDef[] = byCompany.map((c, i) => ({
        key: c.companyId,
        name: c.name,
        color: theme.palette[i % theme.palette.length]!,
      }));
      return {
        data: fillGaps(Object.fromEntries(byCompany.map((c) => [c.companyId, c.series])), bucket),
        series: defs,
      };
    }
    const seriesMap: Record<string, { bucket: string; count: number }[]> = {
      count: query.data?.series ?? [],
    };
    const defs: ChartSeriesDef[] = [{ key: 'count', name: currentLabel, color: theme.palette[0]! }];
    if (hasPrevious) {
      seriesMap.previous = query.data!.previousSeries!;
      defs.push({ key: 'previous', name: previousLabel, color: theme.palette[5]!, dashed: true });
    }
    return { data: fillGaps(seriesMap, bucket), series: defs };
  }, [byCompany, query.data, bucket, theme, currentLabel, previousLabel, hasPrevious]);

  if (widget.type !== 'chart') return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );
  if (data.length === 0) return <WidgetEmpty message={t('tenant.dashboard.widgets.chart.empty')} />;

  const total =
    query.data?.total ?? (query.data?.series ?? []).reduce((sum, p) => sum + p.count, 0);
  const delta =
    hasPrevious && query.data?.previousTotal !== undefined
      ? deltaPercent(total, query.data.previousTotal)
      : null;
  const stacked = byCompany !== undefined;

  // Wykres pozycjonowany absolutnie wewnątrz `relative` rodzica: recharts mierzy
  // box rodzica, a nie odwrotnie — rozmiar wykresu nie wpływa na wysokość komórki
  // siatki, więc nie ma pętli ResponsiveContainer ↔ WidthProvider (toggling
  // scrollbara → zmiana szerokości → re-measure → „Maximum update depth exceeded").
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {!stacked && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xl font-semibold tabular-nums text-text-primary">
            {amount ? formatMoney(total) : total.toLocaleString('pl-PL')}
          </span>
          {delta !== null && <DeltaChip pct={delta} />}
        </div>
      )}
      <div className="relative min-h-[140px] flex-1">
        <div className="absolute inset-0">
          <ResponsiveContainer width="100%" height="100%">
            {renderChart(variant, data, series, theme, {
              stacked,
              amount,
              showLegend: stacked || hasPrevious,
            })}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
